<script setup>

import {computed, onMounted, ref} from "vue";
import PostVideo from "@/Pages/Post/Partials/PostVideo.vue";
import {useVideoStore} from "@/Stores/VideoStore";
import {storeToRefs} from "pinia";
import {useAnalyticsStore} from "@/Stores/AnalyticsStore";
import {ChatBubbleLeftEllipsisIcon, SpeakerWaveIcon, SpeakerXMarkIcon} from "@heroicons/vue/24/outline";

const analyticsStore = useAnalyticsStore();
const props = defineProps({
	model: {type: Object, required: true},
	active: {type: Boolean, default: true},
	index: {type: Number, default: 0}
})

const emit = defineEmits(['ad-playing'])

const isVastAdPlaying = ref(false)

const handleAdPlaying = (playing) => {
	isVastAdPlaying.value = playing
	emit('ad-playing', playing)
}

const videoStore = useVideoStore()
const {video, isMuted} = storeToRefs(videoStore)
const source = computed(() => {
	return {
		'url': props.model.video,
		'type': 'mp4',
		'mimetype': 'video/mp4'
	}
})

const post = computed(() => {
	return {
		uuid: `dsc-${props.model.name}-${props.index}`,
		orientation: 'vertical'
	}
})

onMounted(() => {
	analyticsStore.sendAnalyticsEvent('ad_impression', {
		adType: 'DSC Teaser'
	});
	analyticsStore.sendAnalyticsEvent('DSC Teaser', {
		modelName: props.model.name,
	});
})


const pushGAEvent = () => {
	analyticsStore.sendAnalyticsEvent('ad_click', {
		adType: 'DSC Teaser'
	});
	analyticsStore.sendAnalyticsEvent('dsc_ad_click', {
		modelName: props.model.name,
	});
}
</script>

<template>

	<div class="flex h-full w-full flex-col exclude-poppit">

		<div v-show="!isVastAdPlaying" class="relative z-50 flex h-full w-full flex-col justify-between p-4">
			<div class="flex w-full items-center justify-end">
				<button
					class="h-12 w-12 p-3 rounded-full cursor-pointer outline-hidden bg-white transition-colors duration-200 ease-in text-white bg-opacity-20 hover-hover:hover:bg-opacity-20"
					type="button"
					@click="videoStore.toggleModelAudio()">
					<div class="flex justify-center items-center object-center h-full w-full">
						<SpeakerXMarkIcon v-if="isMuted" class="h-6"/>
						<SpeakerWaveIcon v-else class="h-6"/>
					</div>
				</button>
			</div>

			<a :href="model.url" class="exclude-poppit flex-1" rel="noopener nofollow" target="_blank"
			   @click.once="pushGAEvent">
				<div class=""></div>
			</a>

			<div class="w-full flex flex-col m-auto overflow-hidden">
				<a :href="model.url" class="exclude-poppit" rel="noopener nofollow" target="_blank"
				   @click.once="pushGAEvent">
					<div class="flex flex-row space-x-4 items-center">
						<div class="flex-grow h-0.5 opacity-20 bg-white"></div>
						<div class="flex font-bold cursor-pointer space-x-2 py-4 leading-4 items-center">
							<ChatBubbleLeftEllipsisIcon class="w-5"/>
							<div class="grow-1">Tap to chat</div>
						</div>
						<div class="flex-grow h-0.5 opacity-20 bg-white"></div>
					</div>
					<div class="flex flex-row space-x-4">
						<div>
							<div
								class="h-16 w-16 rounded-full border-2 overflow-hidden border-primary-600 select-none cursor-pointer bg-dark-900">
								<img
									:src="model.thumb" alt=""
									class="object-fit object-center rounded-full overflow-hidden">
							</div>
						</div>
						<div class="flex flex-col justify-center items-start space-y-2">
							<div
								class="inline-flex h-5 items-center justify-center rounded-md bg-black bg-opacity-50 px-2">
					        <span class="relative flex h-2 w-2">
						        <span
							        class="blinking absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                            </span>
								<span class="leading-0 ml-1 text-xs font-bold text-white">LIVE</span>
							</div>
							<div>
								<h1 class="text-base font-bold m-0 p-0 select-none cursor-pointer">
									@{{ model.name }}
								</h1>
							</div>
						</div>
					</div>
				</a>
			</div>
		</div>

		<div class="absolute inset-0 h-full overflow-hidden w-full z-0">
			<PostVideo
				:autoplay="true"
				:is-active="active"
				:source="source"
				:post="post"
				:show-progress="false"
				@ad-playing="handleAdPlaying"
			/>
		</div>
	</div>
</template>
